import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { canAccessChatArea } from '../utils/chatAccess';

export function Navigation() {
  const location = useLocation();
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const stored = window.localStorage.getItem('sidebar-collapsed');
    if (stored !== null) {
      return stored === 'true';
    }

    return window.matchMedia('(max-width: 1024px)').matches;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = document.documentElement;

    if (isMobile) {
      root.style.setProperty('--sidebar-width', '0px');
      return;
    }

    root.style.setProperty(
      '--sidebar-width',
      isCollapsed ? 'var(--sidebar-width-collapsed)' : 'var(--sidebar-width-expanded)'
    );
    window.localStorage.setItem('sidebar-collapsed', String(isCollapsed));
  }, [isCollapsed, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const configItem = {
    path: '/configuracoes',
    label: 'Configurações',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  };

  const menuItems = [
    {
      path: '/whatsapp',
      label: 'Conexões',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    },
    ...(canAccessChatArea(user) ? [{
      path: '/chats',
      label: 'Chats',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v8l-4-2-4 2V10a2 2 0 012-2h2M9 6h6a2 2 0 012 2v10l-4-2-4 2V8a2 2 0 012-2z" />
        </svg>
      )
    }] : []),
    {
      path: '/cumprimento-massa',
      label: 'Central de Cumprimento',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h10M4 17h7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l2 2 4-4" />
        </svg>
      )
    },
    {
      path: '/aprendizado',
      label: 'Aprendizado',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      path: '/mandados',
      label: 'Mandados IA',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3h7l5 5v13a1 1 0 01-1 1H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3v5h5M9 9h2m-2 4h6m-6 4h4" />
        </svg>
      )
    },
    {
      path: '/pagamento',
      label: 'Assinatura',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h10" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h.01M20 17h.01" />
        </svg>
      )
    },
    ...(user?.role === 'SUPERADMIN' ? [{
      path: '/super-admin',
      label: 'Super Admin',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }] : []),
    configItem
  ];

  const activeMenuItem = menuItems.find((item) => location.pathname === item.path);

  if (isMobile) {
    return (
      <>
        <nav
          className="sidebar-navigation shadow-lg flex items-center"
          style={{ background: 'var(--sidebar-bg)' }}
        >
          <div className="w-full flex items-center justify-between px-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                {settings?.iconUrl ? (
                  <img
                    src={settings.iconUrl}
                    alt={settings?.companyName || 'Sistema'}
                    className="max-h-7 max-w-7 object-contain"
                  />
                ) : (
                  <span className="text-white font-bold text-base">
                    {settings?.companyName?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                )}
              </div>
              <div className="text-white min-w-0">
                <p className="text-xs text-white/70 leading-none">Menu</p>
                <p className="text-sm font-semibold truncate">
                  {activeMenuItem?.label || 'Painel'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMobileMenuOpen}
              className="h-10 w-10 rounded-lg text-white/90 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div
              className="absolute inset-x-0 bottom-[var(--top-nav-height)] bg-white rounded-t-2xl shadow-2xl max-h-[70vh] overflow-y-auto p-4"
              onClick={(event) => event.stopPropagation()}
            >
              <ul className="space-y-2">
                {menuItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-3 py-3 transition-all duration-200 ${
                        location.pathname === item.path
                          ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-100'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {item.icon}
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  logout();
                }}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-3 text-red-600 hover:bg-red-50 transition-colors"
                title="Sair"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm font-medium">Sair</span>
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <nav
      className="sidebar-navigation shadow-lg flex flex-col transition-all duration-200"
      style={{ background: 'var(--sidebar-bg)' }}
    >
      <div className="p-4 flex-1">
        <div className={`mb-8 flex items-center ${isCollapsed ? 'justify-center' : 'justify-start gap-3 px-2'}`}>
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
            {settings?.iconUrl ? (
              <img
                src={settings.iconUrl}
                alt={settings?.companyName || 'Sistema'}
                className="max-h-8 max-w-8 object-contain"
              />
            ) : (
              <span className="text-white font-bold text-lg">
                {settings?.companyName?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            )}
          </div>
          {!isCollapsed && (
            <div className="text-white text-sm font-semibold leading-tight">
              <div className="truncate max-w-[140px]">{settings?.companyName || 'Sistema'}</div>
              <div className="text-white/70 text-xs">Painel</div>
            </div>
          )}
        </div>

        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`group relative flex items-center min-w-0 rounded-xl transition-all duration-200 ${
                  isCollapsed ? 'justify-center w-12 h-12 mx-auto' : 'justify-start w-full px-3 py-2.5 gap-3'
                } ${
                  location.pathname === item.path
                    ? 'bg-white text-[var(--astra-dark-blue)] shadow-[0_8px_20px_rgba(2,6,23,0.16)]'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                title={item.label}
              >
                {location.pathname === item.path && !isCollapsed && (
                  <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-[var(--astra-dark-blue)]" aria-hidden="true" />
                )}
                {item.icon}
                {!isCollapsed && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}

                {isCollapsed && (
                  <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.label}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-4 space-y-3 relative z-50">
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          className={`group relative flex items-center rounded-xl transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10 ${
            isCollapsed ? 'justify-center w-12 h-12 mx-auto' : 'justify-start w-full px-3 py-2.5 gap-3'
          }`}
          title={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}
          aria-label={isCollapsed ? 'Expandir menu' : 'Minimizar menu'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {!isCollapsed && <span className="text-sm font-medium">Minimizar</span>}

          {isCollapsed && (
            <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Expandir
            </div>
          )}
        </button>

        <button
          onClick={logout}
          className={`group relative flex items-center rounded-xl transition-all duration-200 text-white/70 hover:text-white hover:bg-red-500/20 z-50 ${
            isCollapsed ? 'justify-center w-12 h-12 mx-auto' : 'justify-start w-full px-3 py-2.5 gap-3'
          }`}
          title="Sair"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {!isCollapsed && <span className="text-sm font-medium">Sair</span>}

          {isCollapsed && (
            <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-sm py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Sair
            </div>
          )}
        </button>
      </div>
    </nav>
  );
}
